import React, { useEffect } from "react";
import { ButtonRenderProps, ContentSection, createListTableColumns, ListTable, Stack, Text } from "sprint-common-web";

import { renderOrderTransferStatus } from "../../../../common/utils/renderOrderTransferStatus";
import { CopyOrderOutputsButton, CopyOrderOutputsButtonProps } from "../buttons/CopyOrderOutputsButton";
import { OrderWorkflowsButton, OrderWorkflowsButtonProps } from "../workflows/OrderWorkflowsButton";
import { OrderOutputsListingItem } from "./OrderOutputsListingItem";
import { RefreshButton, RefreshButtonProps } from "./RefreshButton";
import { useOrderOutputsSection } from "./useOrderOutputsSection";
import { useOrderOutputsSectionFilters } from "./useOrderOutputsSectionFilters";

interface OrderOutputsSectionProps {
    height: number;
}

export const OrderOutputsSection: React.VFC<OrderOutputsSectionProps> = ({ height }: OrderOutputsSectionProps) => {
    const { filters, setSort } = useOrderOutputsSectionFilters();
    const {
        items,
        isLoading,
        orderId,
        status,
        orderProcessingExecutionArn,
        orderDeliveryExecutionArn,
        filterData
    } = useOrderOutputsSection();

    useEffect(() => {
        filterData(filters);
    }, []);

    const columns = createListTableColumns<OrderOutputsListingItem>([
        {
            type: "basic",
            key: "filePath",
            name: "File Path",
            minWidth: 80,
            maxWidth: 380,
            onRender: (item) => (
                <Text tooltip={item?.filePath} variant="small">
                    {item?.filePath}
                </Text>
            )
        },
        {
            type: "basic",
            key: "status",
            name: "Status",
            minWidth: 40,
            maxWidth: 130,
            onRender: renderOrderTransferStatus
        },
        {
            type: "basic",
            key: "completion",
            name: "Progress",
            minWidth: 60,
            maxWidth: 70
        },
        {
            type: "basic",
            key: "orderOutputType",
            name: "Type",
            minWidth: 40,
            maxWidth: 70,
            isSortable: true
        }
    ]);

    const orderProcessingExecutionDetailsButton: ButtonRenderProps<OrderWorkflowsButtonProps> = {
        processingExecutionArn: orderProcessingExecutionArn,
        deliveryExecutionArn: orderDeliveryExecutionArn,
        render: OrderWorkflowsButton
    };

    const refreshButton: ButtonRenderProps<RefreshButtonProps> = {
        onClick: async () => {
            await filterData(filters);
        },
        render: RefreshButton
    };

    const copyOrderOutputsButton: ButtonRenderProps<CopyOrderOutputsButtonProps> = {
        status,
        orderId,
        render: CopyOrderOutputsButton
    };

    return (
        <ContentSection
            header="Outputs"
            style={{ height }}
            buttons={[copyOrderOutputsButton, orderProcessingExecutionDetailsButton, refreshButton]}
            testId={"outputs-section"}
        >
            <Stack styles={{ root: { overflowY: "auto", height: 275 } }}>
                <ListTable
                    columns={columns}
                    items={items}
                    isLoading={isLoading}
                    shimmerLines={1}
                    onSortChange={setSort}
                    sort={{ by: filters.sortBy, order: filters.sortOrder }}
                    noItemsMessage="No outputs"
                />
            </Stack>
        </ContentSection>
    );
};
